import React, { useEffect, useState, useRef } from 'react';

declare global {
  interface Window {
    kakao: any;
  }
}

const KAKAO_SDK_URL = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${process.env.REACT_APP_KAKAO_APP_KEY}&libraries=services&autoload=false`;

const KakaoMap: React.FC = () => {
  const mapRef = useRef<any>(null);
  const [markers, setMarkers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [initialLocation, setInitialLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    // 이미 로드된 경우 (StrictMode 재실행 포함) 바로 초기화
    const init = () => {
      window.kakao.maps.load(() => {
        // 위치 가져오기
        const buildMap = (lat: number, lng: number) => {
          const mapContainer = document.getElementById('map');
          if (!mapContainer) return;

          setInitialLocation({ lat, lng }); // 최초 위치 저장

          const userLocation = new window.kakao.maps.LatLng(lat, lng);
          const map = new window.kakao.maps.Map(mapContainer, {
            center: userLocation,
            level: 5,
          });
          mapRef.current = map; // mapRef에 지도 인스턴스 할당

          // // 사용자 위치에 대한 SVG 마커 생성 (사람 아이콘)
          // const svgIcon =
          //   '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FF0000" width="36px" height="36px"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/><path d="M0 0h24v24H0z" fill="none"/></svg>';
          // const markerImageSrc = `data:image/svg+xml;base64,${window.btoa(svgIcon)}`;
          // const imageSize = new window.kakao.maps.Size(40, 40);
          // const imageOption = { offset: new window.kakao.maps.Point(18, 36) }; // 마커의 하단 중앙을 좌표에 맞춤
          // const markerImage = new window.kakao.maps.MarkerImage(
          //   markerImageSrc,
          //   imageSize,
          //   imageOption
          // );
          // 초기 주변 장소 검색 실행
          searchPlacesNearby(userLocation);
        };

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            pos => buildMap(pos.coords.latitude, pos.coords.longitude),
            error => {
              console.error('Geolocation error: ', error);
              buildMap(37.566826, 126.9786567);
            }
          );
        } else {
          buildMap(37.566826, 126.9786567);
        }
      });
    };

    // 이미 kakao 객체가 있고 maps가 있으면 재사용
    if (window.kakao && window.kakao.maps) {
      init();
      return;
    }

    // 중복 로드 방지: 같은 src 가진 스크립트가 있으면 onload만 연결
    const existed = Array.from(document.getElementsByTagName('script')).find(
      s => s.src === KAKAO_SDK_URL
    ) as HTMLScriptElement | undefined;

    if (existed) {
      if (typeof window.kakao !== 'undefined') {
        init();
      } else {
        existed.addEventListener('load', init, { once: true });
      }
      return;
    }

    // 신규 로드
    const script = document.createElement('script');
    script.src = KAKAO_SDK_URL;
    script.async = true;
    script.addEventListener('load', init, { once: true });
    document.head.appendChild(script);

    // 선택: 언마운트 시 이벤트 정리(스크립트는 그대로 둠)
    return () => {
      script.removeEventListener('load', init);
    };
  }, []);

  const clearMarkers = () => {
    markers.forEach(marker => marker.setMap(null));
    setMarkers([]);
  };

  const searchPlacesNearby = (location: any) => {
    if (!mapRef.current) return;
    clearMarkers();
    const ps = new window.kakao.maps.services.Places();
    const infowindow = new window.kakao.maps.InfoWindow({ removable: true, zIndex: 1 });

    const searchCallback = (data: any, status: any) => {
      if (status === window.kakao.maps.services.Status.OK) {
        const newMarkers: any[] = [];
        data.forEach((place: any) => {
          const placePosition = new window.kakao.maps.LatLng(place.y, place.x);
          const marker = new window.kakao.maps.Marker({
            map: mapRef.current,
            position: placePosition,
          });

          window.kakao.maps.event.addListener(marker, 'click', () => {
            const content = `<div class="infowindow-content"><h4>${place.place_name}</h4><p>${place.road_address_name || place.address_name}</p><p class="phone">${place.phone || '전화번호 정보 없음'}</p><a href="${place.place_url}" target="_blank">카카오맵에서 보기</a></div>`;
            infowindow.setContent(content);
            infowindow.open(mapRef.current, marker);
          });
          newMarkers.push(marker);
        });
        setMarkers(prev => [...prev, ...newMarkers]);
      }
    };

    const opts = { location, radius: 2000, sort: window.kakao.maps.services.SortBy.DISTANCE };
    ps.keywordSearch('정신과', searchCallback, opts);
    ps.keywordSearch('심리상담센터', searchCallback, opts);
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      // 검색어가 없으면 최초 위치로 복귀
      if (initialLocation && mapRef.current) {
        const initialCoords = new window.kakao.maps.LatLng(
          initialLocation.lat,
          initialLocation.lng
        );
        mapRef.current.setCenter(initialCoords);
        searchPlacesNearby(initialCoords);
      }
      return;
    }
    const geocoder = new window.kakao.maps.services.Geocoder();
    geocoder.addressSearch(searchQuery, (result: any, status: any) => {
      if (status === window.kakao.maps.services.Status.OK) {
        const newCoords = new window.kakao.maps.LatLng(result[0].y, result[0].x);
        mapRef.current.setCenter(newCoords);
        searchPlacesNearby(newCoords);
      } else {
        alert('검색 결과가 없습니다. 주소를 다시 확인해주세요.');
      }
    });
  };

  return (
    <div className="kakao-map-wrapper">
      <div className="map-search-bar">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          placeholder="동, 면, 읍 또는 주소를 입력하세요"
        />
        <button onClick={handleSearch}>검색</button>
      </div>
      <div id="map" style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default KakaoMap;
